import { useUser } from "@clerk/clerk-react";
import { useCallback } from "react";
import { ONBOARDING_TOUR_SEEN_KEY } from "@/constants/tour";
import type { OnboardingTourSeen } from "@/interfaces/tour";
import { hasSeenOnboardingTour } from "@/utils/tour-helper";

/**
 * Reads and writes the "has this user seen the onboarding welcome tour" flag.
 * Persisted on `user.unsafeMetadata`, so the flag survives logout/login without any backend route.
 */
export function useOnboardingTourSeen(): OnboardingTourSeen {
  const { user, isLoaded } = useUser();

  const hasSeen = hasSeenOnboardingTour(user?.unsafeMetadata);

  const markSeen = useCallback(async (): Promise<void> => {
    if (!user || hasSeen) return;

    try {
      await user.update({
        unsafeMetadata: { ...user.unsafeMetadata, [ONBOARDING_TOUR_SEEN_KEY]: true },
      });
    } catch (error) {
      // A network hiccup writing a UI preference must not be able to break the dashboard.
      console.warn("Failed to persist onboarding tour seen flag", error);
    }
  }, [user, hasSeen]);

  return { isLoaded, hasSeen, markSeen };
}
